import { useCallback, useEffect, useState } from 'react';

import { createLogger } from '../../utils/logger';
import { fetchAdsJobImages } from '../api';
import { calculateAdsJobCost } from '../hooks/useAdsStats';
import type {
  AdDesign,
  AdInfo,
  AdsJobConfigSnapshot,
  AdsJobImage,
  AdsJobImagesResponse,
  PalettePreset,
  VehicleMetadata,
} from '../types';

// Mirrors `HistoricalTable.formatCost` — kept in sync so the same scan
// renders the same number in the row and inside the detail modal. A
// completed job that recorded no usage (legacy rows from before cost
// tracking was wired up) renders as ``-`` instead of ``$0.00`` so it's
// visually distinct from a real, billed-but-tiny job.
function formatCost(cost: number | null | undefined): string {
  if (cost == null || cost === 0) return '-';
  if (cost < 0.01) return '<$0.01';
  return `$${cost.toFixed(2)}`;
}

const log = createLogger('AdsImagesModal');

interface Props {
  jobId: string;
  scanId: string;
  onClose: () => void;
}

function inferExtension(url: string): string {
  try {
    const path = new URL(url, window.location.origin).pathname;
    const match = path.match(/\.([a-zA-Z0-9]{1,5})$/);
    if (match) return match[1].toLowerCase();
  } catch {
    // ignore
  }
  return 'png';
}

async function downloadImage(image: AdsJobImage, scanId: string): Promise<void> {
  if (!image.url) return;
  const ext = inferExtension(image.url);
  const filename = `${scanId}_${image.id}.${ext}`;
  try {
    const resp = await fetch(image.url);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const blob = await resp.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  } catch (err) {
    log.error('download failed', err);
    if (image.url) window.open(image.url, '_blank', 'noopener,noreferrer');
  }
}

function ImageCard({
  image,
  scanId,
  onOpen,
}: {
  image: AdsJobImage;
  scanId: string;
  onOpen: () => void;
}) {
  const [downloading, setDownloading] = useState(false);
  const [errored, setErrored] = useState(false);
  const previewClickable = Boolean(image.url) && !errored;

  return (
    <div className="bg-slate-50 rounded-lg overflow-hidden border border-slate-200 flex flex-col">
      <button
        type="button"
        onClick={previewClickable ? onOpen : undefined}
        disabled={!previewClickable}
        aria-label={previewClickable ? `Open ${image.label} at full size` : image.label}
        className={`aspect-video bg-slate-100 flex items-center justify-center overflow-hidden focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
          previewClickable
            ? 'cursor-zoom-in group'
            : 'cursor-default'
        }`}
      >
        {previewClickable ? (
          <img
            src={image.url ?? undefined}
            alt={image.label}
            className="max-h-full max-w-full object-contain transition-transform duration-200 group-hover:scale-[1.02]"
            loading="lazy"
            onError={() => setErrored(true)}
          />
        ) : (
          <div className="text-sm text-slate-400">Preview unavailable</div>
        )}
      </button>
      <div className="px-3 py-2 flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-900 truncate">
            {image.label}
          </div>
          <div className="text-xs text-slate-500">
            {image.width}×{image.height} · {image.aspect_ratio}
          </div>
        </div>
        <button
          type="button"
          disabled={!image.url || downloading}
          onClick={async () => {
            setDownloading(true);
            try {
              await downloadImage(image, scanId);
            } finally {
              setDownloading(false);
            }
          }}
          className="px-2.5 py-1 text-xs font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {downloading ? 'Downloading…' : 'Download'}
        </button>
      </div>
    </div>
  );
}

/**
 * Fullscreen lightbox shown on top of the AdsImagesModal grid.
 *
 * Click a thumbnail in the grid -> this renders. Arrow keys / on-screen
 * chevrons step through neighbours; Escape (or clicking the backdrop)
 * closes back to the grid. We keep the parent grid mounted under the
 * backdrop so dismissing the lightbox is instant and there's no flash
 * of "loading…".
 */
function Lightbox({
  images,
  index,
  scanId,
  onClose,
  onPrev,
  onNext,
}: {
  images: AdsJobImage[];
  index: number;
  scanId: string;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  const image = images[index];
  const [downloading, setDownloading] = useState(false);
  const [errored, setErrored] = useState(false);

  // Reset the `errored` flag whenever we step to a new image — a
  // previously-failing URL shouldn't poison the rest of the carousel.
  useEffect(() => {
    setErrored(false);
  }, [index]);

  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.preventDefault();
        onClose();
      } else if (ev.key === 'ArrowLeft') {
        ev.preventDefault();
        onPrev();
      } else if (ev.key === 'ArrowRight') {
        ev.preventDefault();
        onNext();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, onPrev, onNext]);

  if (!image) return null;

  const total = images.length;
  const hasNeighbours = total > 1;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      <button
        type="button"
        aria-label="Close lightbox"
        className="absolute inset-0 bg-black/85"
        onClick={onClose}
      />

      <div className="absolute top-3 left-4 right-4 flex items-center justify-between gap-3 text-white pointer-events-none">
        <div className="min-w-0 pointer-events-auto">
          <div className="text-sm font-medium truncate">{image.label}</div>
          <div className="text-xs text-white/70 truncate">
            {image.width}×{image.height} · {image.aspect_ratio}
            {hasNeighbours && ` · ${index + 1} / ${total}`}
          </div>
        </div>
        <div className="flex items-center gap-2 pointer-events-auto">
          <button
            type="button"
            disabled={!image.url || downloading}
            onClick={async () => {
              setDownloading(true);
              try {
                await downloadImage(image, scanId);
              } finally {
                setDownloading(false);
              }
            }}
            className="px-3 py-1.5 text-xs font-medium bg-white/15 hover:bg-white/25 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed backdrop-blur-sm"
          >
            {downloading ? 'Downloading…' : 'Download'}
          </button>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 flex items-center justify-center rounded-md bg-white/15 hover:bg-white/25 text-white transition-colors backdrop-blur-sm"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {hasNeighbours && (
        <>
          <button
            type="button"
            onClick={onPrev}
            aria-label="Previous image"
            className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 text-white transition-colors backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-white/60"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            type="button"
            onClick={onNext}
            aria-label="Next image"
            className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 w-11 h-11 flex items-center justify-center rounded-full bg-white/15 hover:bg-white/25 text-white transition-colors backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-white/60"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </>
      )}

      <div
        className="relative pointer-events-none flex items-center justify-center w-full h-full px-6 sm:px-20 py-16"
        // Stop clicks on the image itself from bubbling to the backdrop
        // (which would close the lightbox).
        onClick={(ev) => ev.stopPropagation()}
      >
        {image.url && !errored ? (
          <img
            src={image.url}
            alt={image.label}
            className="max-h-full max-w-full object-contain shadow-2xl rounded-md pointer-events-auto select-none"
            draggable={false}
            onError={() => setErrored(true)}
          />
        ) : (
          <div className="text-white/80 text-sm pointer-events-auto">Preview unavailable</div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// "What was fed in" panel — renders the persisted job config (vehicle
// metadata, listing copy, design choices) alongside the resolved
// palette swatches and engine + prompt. The History modal shows this
// above the rendered creatives so The Master can see at a glance the
// exact inputs that produced the visible outputs.
// =============================================================================

const VEHICLE_FIELD_ORDER: (keyof VehicleMetadata)[] = [
  'make',
  'model',
  'year',
  'color',
  'body_type',
  'fuel_type',
  'trim',
  'vin',
];

const INFO_FIELD_ORDER: (keyof AdInfo)[] = [
  'price',
  'mileage',
  'location',
  'phone',
  'email',
  'free_text',
];

function prettyKey(key: string): string {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function valueOrDash(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'string') return value.trim() === '' ? '—' : value;
  if (Array.isArray(value)) return value.length === 0 ? '—' : value.join(', ');
  return String(value);
}

function MetaList({
  fields,
  source,
}: {
  fields: string[];
  source: Record<string, unknown> | null | undefined;
}) {
  if (!source) {
    return <p className="text-xs text-slate-400 italic">Not recorded.</p>;
  }
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs">
      {fields.map((key) => (
        <div key={key} className="contents">
          <dt className="text-slate-500">{prettyKey(key)}</dt>
          <dd className="text-slate-800 break-words">
            {valueOrDash(source[key])}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function PaletteStrip({
  palette,
  fallbackId,
}: {
  palette: PalettePreset | null | undefined;
  fallbackId: string | null | undefined;
}) {
  // No palette resolved (legacy row or removed-from-yaml id) — still
  // surface the raw id so The Master knows what was originally chosen.
  if (!palette) {
    if (!fallbackId) {
      return <p className="text-xs text-slate-400 italic">No palette recorded.</p>;
    }
    return (
      <div className="text-xs text-slate-600">
        <span className="font-mono">{fallbackId}</span>{' '}
        <span className="text-slate-400">(swatches unavailable)</span>
      </div>
    );
  }
  return (
    <div className="space-y-1.5">
      <div className="text-xs font-medium text-slate-800">
        {palette.label}{' '}
        <span className="font-mono text-slate-400">({palette.id})</span>
      </div>
      <div className="flex h-8 rounded-md overflow-hidden border border-slate-200">
        {palette.swatches.map((hex, i) => (
          <div
            key={`${hex}-${i}`}
            className="flex-1 relative group"
            style={{ background: hex }}
            title={hex}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] font-mono text-slate-500">
        {palette.swatches.map((hex, i) => (
          <span key={`${hex}-label-${i}`}>{hex}</span>
        ))}
      </div>
    </div>
  );
}

function DesignList({ design }: { design: AdDesign | null | undefined }) {
  if (!design) {
    return <p className="text-xs text-slate-400 italic">Not recorded.</p>;
  }
  const rows: Array<[string, string]> = [
    ['Orientation', valueOrDash(design.orientation)],
    ['Mood', valueOrDash(design.mood)],
    ['Style', valueOrDash(design.style)],
    ['Formats', valueOrDash(design.formats)],
    ['Logo corner', valueOrDash(design.logo_corner)],
  ];
  return (
    <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-xs">
      {rows.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-slate-500">{label}</dt>
          <dd className="text-slate-800 break-words">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function PromptBlock({ prompt }: { prompt: string | null | undefined }) {
  const [expanded, setExpanded] = useState(false);
  if (!prompt) return null;
  return (
    <details
      className="bg-slate-50 border border-slate-200 rounded-md text-xs"
      open={expanded}
      onToggle={(e) => setExpanded((e.target as HTMLDetailsElement).open)}
    >
      <summary className="px-3 py-2 cursor-pointer font-medium text-slate-700 select-none">
        {expanded ? 'Hide prompt' : 'Show prompt'}
      </summary>
      <pre className="px-3 pb-3 pt-1 whitespace-pre-wrap font-mono text-[11px] text-slate-700">
        {prompt}
      </pre>
    </details>
  );
}

function JobInputsPanel({
  config,
  palette,
  vehicleInfo,
  engine,
  prompt,
}: {
  config: AdsJobConfigSnapshot | null | undefined;
  palette: PalettePreset | null | undefined;
  vehicleInfo: VehicleMetadata | null | undefined;
  engine: string | null | undefined;
  prompt: string | null | undefined;
}) {
  // Prefer the resolved-at-generate-time vehicle info (what the
  // engine actually saw) and fall back to whatever the submitter
  // posted via the wizard. They're usually identical, but the
  // engine-time copy is the authoritative "what produced these
  // images" answer.
  const vehicle =
    vehicleInfo ??
    (config?.metadata as VehicleMetadata | undefined) ??
    null;

  const hasAnything =
    Boolean(config) ||
    Boolean(palette) ||
    Boolean(vehicle) ||
    Boolean(engine) ||
    Boolean(prompt);
  if (!hasAnything) return null;

  return (
    <section className="mb-5 rounded-xl border border-slate-200 bg-white">
      <header className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900">
            What was fed in
          </h3>
          <p className="text-[11px] text-slate-500">
            Configuration, palette, and metadata used to render these creatives.
          </p>
        </div>
        {engine && (
          <span className="text-[11px] text-slate-500">
            Engine: <span className="font-mono text-slate-700">{engine}</span>
          </span>
        )}
      </header>

      <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-2">
            Vehicle
          </h4>
          <MetaList
            fields={VEHICLE_FIELD_ORDER as string[]}
            source={vehicle as Record<string, unknown> | null}
          />
        </div>

        <div>
          <h4 className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-2">
            Listing
          </h4>
          <MetaList
            fields={INFO_FIELD_ORDER as string[]}
            source={(config?.info ?? null) as Record<string, unknown> | null}
          />
        </div>

        <div>
          <h4 className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-2">
            Design
          </h4>
          <DesignList design={config?.design ?? null} />
        </div>

        <div>
          <h4 className="text-[11px] uppercase tracking-wide font-semibold text-slate-500 mb-2">
            Palette
          </h4>
          <PaletteStrip
            palette={palette ?? null}
            fallbackId={config?.design?.palette ?? null}
          />
        </div>
      </div>

      {prompt && (
        <div className="px-4 pb-4">
          <PromptBlock prompt={prompt} />
        </div>
      )}
    </section>
  );
}

/**
 * Modal showing every rendered creative for a completed ads job.
 *
 * Used by the History page (clicking a row) and by the Generate
 * wizard's Step 5 (Review). All formats are presented in a
 * responsive grid; each card has a per-image Download button that
 * fetches the URL into a blob and triggers a save-as.
 */
export function AdsImagesModal({ jobId, scanId, onClose }: Props) {
  const [data, setData] = useState<AdsJobImagesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchAdsJobImages(jobId)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((ex) => {
        if (!cancelled) setError(ex instanceof Error ? ex.message : 'Failed to load images');
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  // Only formats with an actual `url` participate in the lightbox so
  // we don't navigate to "Preview unavailable" placeholders.
  const formatsWithUrl = data?.formats.filter((f) => Boolean(f.url)) ?? [];

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const showPrev = useCallback(() => {
    setLightboxIndex((idx) =>
      idx === null || formatsWithUrl.length === 0
        ? idx
        : (idx - 1 + formatsWithUrl.length) % formatsWithUrl.length,
    );
  }, [formatsWithUrl.length]);
  const showNext = useCallback(() => {
    setLightboxIndex((idx) =>
      idx === null || formatsWithUrl.length === 0
        ? idx
        : (idx + 1) % formatsWithUrl.length,
    );
  }, [formatsWithUrl.length]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900">Generated Ads</h2>
            <p className="text-sm text-slate-500">
              Scan <span className="font-mono">{scanId}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 transition-colors"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {error && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm mb-4">
              {error}
            </div>
          )}
          {!data && !error && (
            <div className="p-12 text-center text-slate-500">Loading…</div>
          )}
          {data && (
            <JobInputsPanel
              config={data.config}
              palette={data.palette}
              vehicleInfo={data.vehicle_info}
              engine={data.engine}
              prompt={data.prompt}
            />
          )}
          {data && data.formats.length === 0 && (
            <div className="p-12 text-center text-slate-500">No outputs available.</div>
          )}
          {data && data.formats.length > 0 && (
            <>
              <p className="text-xs text-slate-500 mb-3">
                Click any image to view at full size.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {data.formats.map((fmt) => {
                  const lightboxPos = formatsWithUrl.findIndex((f) => f.id === fmt.id);
                  return (
                    <ImageCard
                      key={fmt.id}
                      image={fmt}
                      scanId={scanId}
                      onOpen={() => {
                        if (lightboxPos >= 0) setLightboxIndex(lightboxPos);
                      }}
                    />
                  );
                })}
              </div>
            </>
          )}
          {data && (data.engine || data.fal_usage || data.gemini_usage) && (
            <div className="mt-6 text-xs text-slate-500 flex flex-wrap items-center gap-x-4 gap-y-1">
              {data.engine && (
                <span>
                  Engine: <span className="font-mono">{data.engine}</span>
                </span>
              )}
              <span>
                Cost:{' '}
                <span className="font-mono">
                  {formatCost(calculateAdsJobCost(data))}
                </span>
              </span>
            </div>
          )}
        </div>
      </div>

      {lightboxIndex !== null && formatsWithUrl.length > 0 && (
        <Lightbox
          images={formatsWithUrl}
          index={Math.min(lightboxIndex, formatsWithUrl.length - 1)}
          scanId={scanId}
          onClose={closeLightbox}
          onPrev={showPrev}
          onNext={showNext}
        />
      )}
    </div>
  );
}
